// Overview: the last 30 days against the 30 before, a daily chart, what needs attention, and the latest
// orders, accounts and pages. Everything fits one window on desktop.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { ALL_TOPICS, UNITS } from '@mathladder/shared';
import { api } from '../api';
import { ago, count, money, plural, useLoad, useTitle } from '../lib';
import { Alert, Card, Delta, Empty, Loading, Page, Stat } from '../ui';

type Metrics = Record<string, number>;
interface Day { day: string; signups: number; documents: number; orders: number; revenue_cents: number; views: number; downloads: number }
interface Data {
  metrics: Metrics; daily: Day[];
  recentOrders: { id: string; receipt_no: number; status: string; total_cents: number; refunded_cents: number; paid_at: string; email: string }[];
  recentAccounts: { id: string; email: string; created_at: string; email_verified_at: string | null; students: number }[];
  topPages: { path: string; n: number }[];
}

const NAMES = new Map<string, string>([
  ['/', 'Home'], ['/worksheets/', 'Worksheets'], ['/pricing/', 'Pricing'], ['/climb-the-ladder/', 'Climb the ladder'], ['/faq/', 'FAQ'],
  ...ALL_TOPICS.map((t) => [t.path, t.name] as [string, string]),
  ...UNITS.map((u) => [u.path, u.name] as [string, string]),
]);
export const pageName = (p: string) => NAMES.get(p) ?? p;
const dayLabel = (d: string, o: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, o);

const SERIES = {
  revenue: { label: 'Revenue', key: 'revenue_cents', fmt: money },
  signups: { label: 'Sign-ups', key: 'signups', fmt: count },
  documents: { label: 'Worksheets', key: 'documents', fmt: count },
  views: { label: 'Page views', key: 'views', fmt: count },
} as const;
type SeriesId = keyof typeof SERIES;

export function Overview() {
  useTitle('Overview');
  const { data, error, loading, reload } = useLoad(() => api.get<Data>('/admin/metrics'));
  return (
    <Page title="Overview" sub="Last 30 days"
      actions={<button className="btn btn-sm" onClick={() => void reload()} disabled={loading}><RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh</button>}>
      <Alert>{error}</Alert>
      {!data ? <Loading /> : <Body d={data} />}
    </Page>
  );
}

function Body({ d }: { d: Data }) {
  const m = d.metrics;
  const last = d.daily.slice(-30), prev = d.daily.slice(0, -30);
  const sum = (rows: Day[], k: keyof Day) => rows.reduce((a, r) => a + Number(r[k]), 0);
  const tile = (k: keyof Day) => ({ now: sum(last, k), before: sum(prev, k), trend: last.map((r) => Number(r[k])) });
  const rev = tile('revenue_cents'), sign = tile('signups'), docs = tile('documents'), views = tile('views');
  return (
    <>
      <div className="stats">
        <Stat label="Revenue" value={money(rev.now)} trend={rev.trend} delta={<Delta now={rev.now} before={rev.before} />} to="/sales" />
        <Stat label="New accounts" value={count(sign.now)} trend={sign.trend} delta={<Delta now={sign.now} before={sign.before} />} to="/customers" />
        <Stat label="Unlimited accounts" value={count(m.active_passes)} foot={m.passes_ending_30d ? `${m.passes_ending_30d} yearly plans end within 30 days` : `${count(m.accounts)} accounts in all`} to="/customers?plan=1" />
        <Stat label="Worksheets made" value={count(docs.now)} trend={docs.trend} delta={<Delta now={docs.now} before={docs.before} />} to="/activity/documents" />
        <Stat label="Page views" value={count(views.now)} trend={views.trend} delta={<Delta now={views.now} before={views.before} />} to="/activity" />
      </div>
      <div className="ov">
        <Card className="c8" title="Daily" actions={null}><Chart days={last} /></Card>
        <Card className="c4" title="Health" sub="Problems link to where they are" flush><Health m={m} /></Card>
        <Card className="c4" title="Latest orders" actions={<Link to="/sales">All orders</Link>} flush>
          {!d.recentOrders.length ? <Empty>No paid orders yet.</Empty> : (
            <ul className="rows">
              {d.recentOrders.map((o) => (
                <li key={o.id}><Link to={`/sales/orders/${o.id}`}>
                  <span className="min0"><span className="truncate" style={{ display: 'block' }}>{o.email}</span><span className="sub">#{o.receipt_no} · {ago(o.paid_at)}{o.status !== 'paid' ? ` · ${o.status.replace('_', ' ')}` : ''}</span></span>
                  <span className="end">{money(o.total_cents - o.refunded_cents)}</span>
                </Link></li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="c4" title="Newest accounts" actions={<Link to="/customers">All customers</Link>} flush>
          {!d.recentAccounts.length ? <Empty>No accounts yet.</Empty> : (
            <ul className="rows">
              {d.recentAccounts.map((a) => (
                <li key={a.id}><Link to={`/customers/${a.id}`}>
                  <span className="min0"><span className="truncate" style={{ display: 'block' }}>{a.email}</span><span className="sub">{ago(a.created_at)} · {plural(a.students, 'learner')}</span></span>
                  {!a.email_verified_at && <span className="badge badge-warn">unconfirmed</span>}
                </Link></li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="c4" title="Top pages" sub="Views, last 30 days" actions={<Link to="/activity">Traffic</Link>} flush>
          {!d.topPages.length ? <Empty>No page views yet.</Empty> : (
            <ul className="rows">
              {d.topPages.map((p) => (
                <li key={p.path}><div className="rowi"><span className="truncate">{pageName(p.path)}</span><span className="end">{count(p.n)}</span></div></li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function Health({ m }: { m: Metrics }) {
  const items: { label: string; v: number; to: string; tone: '' | 'warn' | 'bad'; okText?: string }[] = [
    { label: 'Payment disputes, 30 days', v: m.disputes_30d, to: '/system?source=stripe', tone: m.disputes_30d ? 'bad' : '' },
    { label: 'Errors, 24 hours', v: m.errors_24h, to: '/system', tone: m.errors_24h ? 'bad' : '' },
    { label: 'Stuck in the queue (10+ min)', v: m.stuck, to: '/activity/documents?status=queued', tone: m.stuck ? 'bad' : '' },
    { label: 'Failed worksheets, 7 days', v: m.docs_failed_7d, to: '/activity/documents?status=failed', tone: m.docs_failed_7d ? 'bad' : '' },
    { label: 'Failed verification, 7 days', v: m.failed_seeds_7d, to: '/activity/generator', tone: m.failed_seeds_7d ? 'warn' : '' },
    { label: 'In the queue now', v: m.queue, to: '/activity/documents?status=queued', tone: '' },
    { label: 'Unconfirmed accounts', v: m.unverified, to: '/customers', tone: '' },
  ];
  return (
    <ul className="health">
      {items.map((i) => (
        <li key={i.label} className={i.tone}><Link to={i.to}><span className="dot" aria-hidden="true" />{i.label}<span className="v">{count(i.v)}</span></Link></li>
      ))}
    </ul>
  );
}

/** A round number at or above n for the chart's top gridline. */
function niceMax(n: number) {
  if (n <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(n));
  return ([1, 2, 2.5, 5, 10].find((f) => f * p >= n) ?? 10) * p;
}

function Chart({ days }: { days: Day[] }) {
  const [id, setId] = useState<SeriesId>('revenue');
  const [hover, setHover] = useState<number | null>(null);
  const s = SERIES[id];
  const v = (d: Day) => Number(d[s.key]);
  const top = niceMax(Math.max(...days.map(v)));
  const h = hover != null ? days[hover] : null;
  return (
    <div className="chart">
      <div className="chart-top">
        <span className="chart-total">{s.fmt(days.reduce((a, d) => a + v(d), 0))}</span>
        <span className="small muted">{s.label.toLowerCase()} in 30 days</span>
        <span style={{ marginLeft: 'auto' }} className="seg" role="group" aria-label="Chart">
          {(Object.keys(SERIES) as SeriesId[]).map((k) => <button key={k} type="button" aria-pressed={k === id} onClick={() => setId(k)}>{SERIES[k].label}</button>)}
        </span>
      </div>
      <div className="chart-plot" aria-hidden="true" onMouseLeave={() => setHover(null)}>
        {[1, 0.5, 0].map((f) => <div key={f} className={`gl ${f ? '' : 'base'}`} style={{ bottom: `${f * 100}%` }}><span>{f ? s.fmt(top * f) : '0'}</span></div>)}
        <div className="chart-bars">
          {days.map((d, i) => <div key={d.day} className={`col ${hover === i ? 'on' : ''}`} onMouseEnter={() => setHover(i)}><span style={{ height: `${(v(d) / top) * 100}%` }} /></div>)}
        </div>
        {h && (
          <div className="chart-tip" style={{ left: `${((hover! + 0.5) / days.length) * 100}%`, transform: `translate(${hover! < 4 ? -15 : hover! > days.length - 5 ? -85 : -50}%, -100%)` }}>
            <strong>{s.fmt(v(h))}</strong> <span className="muted">{dayLabel(h.day, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
            {id === 'revenue' && h.orders > 0 && <div className="muted">{plural(h.orders, 'order')}</div>}
          </div>
        )}
      </div>
      <div className="chart-x" aria-hidden="true"><span>{dayLabel(days[0].day)}</span><span>{dayLabel(days[days.length - 1].day)}</span></div>
      <table className="sr-only"><caption>{s.label} per day</caption><tbody>{days.map((d) => <tr key={d.day}><th scope="row">{dayLabel(d.day)}</th><td>{s.fmt(v(d))}</td></tr>)}</tbody></table>
    </div>
  );
}
